import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/shared/utils';

type PlayerControlButtonVariant = 'default' | 'primary' | 'ghost';
type PlayerControlButtonSize = 'default' | 'lg';

export interface PlayerControlButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PlayerControlButtonVariant;
  size?: PlayerControlButtonSize;
}

const variantClasses: Record<PlayerControlButtonVariant, string> = {
  default:
    'bg-white text-slate-600 hover:text-slate-900 border border-slate-200 shadow-sm',
  primary:
    'bg-white text-slate-900 shadow-[0_18px_35px_rgba(15,23,42,0.12)]',
  ghost:
    'bg-transparent text-slate-500 hover:text-slate-800 border border-transparent',
};

const sizeClasses: Record<PlayerControlButtonSize, string> = {
  default: 'h-12 w-12 text-xl',
  lg: 'h-16 w-16 text-2xl',
};

export const PlayerControlButton = forwardRef<
  HTMLButtonElement,
  PlayerControlButtonProps
>(({ className, variant = 'default', size = 'default', ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  );
});

PlayerControlButton.displayName = 'PlayerControlButton';
